import Settings from "./Settings";
import { AuthToken } from "./AuthToken";

import ProcessingResult from "../framework/ProcessingResult";

import TransformationMapper from "./mappers/TransformationMapper";
import DataTableReadMapper from "./mappers/DataTableReadMapper";
import GroupFilterMapper from "./mappers/GroupFilterMapper";

import DataTableReadDto from "../classes/dtos/pathcommonmodule/DataTableReadDto";

import GetPathReportingDetailResult from "../classes/api/result/pathreportingmodule/GetPathReportingDetailResult";
import UpdatePathReportingDetailResult from "../classes/api/result/pathreportingmodule/UpdatePathReportingDetailResult";

import GetPathReportingCapabilitiesResult from "../classes/api/result/pathreportingmodule/GetPathReportingCapabilitiesResult";

import PathReportingMappingDto from "../classes/dtos/pathreportingmodule/PathReportingMappingDto";
import ScheduleDto from "../classes/dtos/schedulermodule/ScheduleDto";

import FieldReferenceIdAndKeyDto from "../classes/dtos/FieldReferenceIdAndKeyDto";
import FieldTypeReferenceDto from "../classes/dtos/FieldTypeReferenceDto";
import PathReportingMappingCapabilitiesDto from "../classes/dtos/pathreportingmodule/PathReportingMappingCapabilitiesDto";

const PathReportingApi = {

    GetPathReportingDetailAsync: async function (pathId: number): Promise<ProcessingResult<GetPathReportingDetailResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathReporting/GetPathReportingDetail?pathId=" + pathId.toString(), {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let source = DataTableReadMapper.ToDto(data.source);
            let mappings = data.mappings.map((e: any) => new PathReportingMappingDto(e.fieldReferenceId, e.transformationLogicId, e.transformationParameters, e.displayName, e.isSelected, GroupFilterMapper.RowFiltersToDtos(e.filters), GroupFilterMapper.DeclaredVariablesFromResponse(e.declaredVariables)));
            let schedule = new ScheduleDto(data.schedule.isActive, data.schedule.scheduleType, data.schedule.interval, data.schedule.time);
            return ProcessingResult.Ok(new GetPathReportingDetailResult(data.id, data.name, source, mappings, schedule, data.lastVersionId, data.publishedVersionId, data.advancedFilterMode ?? false));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    UpdatePathReportingDetailAsync: async function (pathId: number, name: string, mappings: PathReportingMappingDto[], source: DataTableReadDto, schedule: ScheduleDto, advancedFilterMode: boolean, publish: boolean): Promise<ProcessingResult<UpdatePathReportingDetailResult>> {
        let authSession = await AuthToken.get();

        let time = (schedule.Time / (60 * 1000)) - new Date().getTimezoneOffset();

        const response = await fetch(Settings.api_url + "/PathReporting/UpdatePathReportingDetail", {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathId': pathId,
                'name': name,
                'source': DataTableReadMapper.ToPayload(source),
                'mappings': mappings.map((e) => ({ fieldReferenceId: e.FieldReferenceId, transformationLogicId: e.TransformationLogicId, transformationParameters: e.TransformationParameters, displayName: e.DisplayName, isSelected: e.IsSelected, filters: GroupFilterMapper.RowFiltersToPayload(e.Filters), declaredVariables: GroupFilterMapper.DeclaredVariablesToPayload(e.DeclaredVariables) })),
                'schedule': {
                    'isActive': schedule.IsActive,
                    'scheduleType': schedule.Type,
                    'interval': schedule.Interval,
                    'time': time
                },
                'advancedFilterMode': advancedFilterMode,
                'Publish': publish
            })
        });

        if (response.status === 200) {
            let data = await response.json();
            return ProcessingResult.Ok(new UpdatePathReportingDetailResult(data.id, data.isPublished));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    GetPathReportingCapabilitiesAsync: async function (tableReferenceSourceId: number | null, pathId: number | null): Promise<ProcessingResult<GetPathReportingCapabilitiesResult>> {
        let authSession = await AuthToken.get();

        let url = "/PathReporting/GetPathReportingCapabilities";
        let separator = "?";

        if (tableReferenceSourceId !== null) { url = url + separator + "TableReferenceSourceId=" + tableReferenceSourceId; separator = "&"; }
        if (pathId !== null) { url = url + separator + "PathId=" + pathId; }

        const response = await fetch(Settings.api_url + url, {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let sourceFields = data.sourceFields.map((e: any) => new FieldReferenceIdAndKeyDto(e.id, e.sourceKey, e.datasetKey, e.name, new FieldTypeReferenceDto(e.type.type, e.type.isMultiple, e.type.typeLabel, e.type.isNullable, e.type.maxLength, e.type.isReadOnly, e.type.isCalculated, e.type.isBusinessRequired, e.type.isPrimaryKey, e.type.originTypeLabel, e.type.linkTableId)));
            let mappingsCapabilities = data.mappingsCapabilities.map((e: any) => new PathReportingMappingCapabilitiesDto(e.sourceFieldId, e.transformationLogicIds));
            let transformationsCapabilities = data.transformationsCapabilities.map((e: any) => TransformationMapper.ToDto(e));
            return ProcessingResult.Ok(new GetPathReportingCapabilitiesResult(sourceFields, mappingsCapabilities, transformationsCapabilities));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    }
};

export default PathReportingApi;